import React, { useState, useEffect } from 'react';
import { IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import * as api from 'services/oldApiService';
import { useAuth } from 'context/AuthContext';
import { useGetUserRoles } from 'hooks/useLookups';
import loginTop from 'assets/img/MS-login-top-img.png';
import loginRight from 'assets/img/MS-Login-right-img.png';

const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$#!.%*+=(){}?&,:;'-])[A-Za-z\d@$#!.%+=(){}*?&,:;'-]{8,}$/;

const schema = yup.object({
  first_name: yup.string().required('First name is required'),
  last_name: yup.string().required('Last name is required'),
  user_role_id: yup.string().required('Role is required'),
  password: yup
    .string()
    .required('Password is required')
    .matches(
      passwordRegex,
      'Must contain 8+ characters, one uppercase, one number, and one special character'
    ),
});

type FormValues = {
  first_name: string;
  last_name: string;
  user_role_id: string;
  password: string;
};

type VerifyState =
  | { status: 'loading' }
  | { status: 'new_user'; email: string; userId: number }
  | { status: 'existing_user'; email: string }
  | { status: 'error'; message: string };

export default function InviteSignupPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { data: roles = [] } = useGetUserRoles();

  // Params come directly from the URL so no API call is needed before routing here
  const { userId, code, key } = useParams<{ userId: string; code: string; key: string }>();

  const [verifyState, setVerifyState] = useState<VerifyState>({ status: 'loading' });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  });

  const passwordValue = watch('password');

  // Verify the invitation on mount to get the email and user status
  useEffect(() => {
    const verify = async () => {
      try {
        // verifyInvitation already unwraps the inner `data` field, so
        // the result is { email, old_user, user_id } directly.
        const result = await (api as any).verifyInvitation(userId, key, code);

        if (result?.old_user === false) {
          setVerifyState({ status: 'new_user', email: result.email, userId: result.user_id });
        } else if (result?.old_user === true) {
          setVerifyState({ status: 'existing_user', email: result.email });
        } else {
          setVerifyState({ status: 'error', message: 'This invitation link is invalid or has expired.' });
        }
      } catch {
        setVerifyState({ status: 'error', message: 'This invitation link is invalid or has expired.' });
      }
    };

    verify();
  }, [userId, code, key]);

  const onSubmit = async (values: FormValues) => {
    if (verifyState.status !== 'new_user') return;

    setSubmitError('');
    setIsSubmitting(true);

    // The stub user already exists and is logged in on the server (session set by
    // verifyInvitation). We complete their profile via updateUserInfo rather than
    // creating a new account via signUp.
    // Sending `verified: true` triggers set_invited_user_as_verified on the backend
    // and returns the full user object in result.result — no email verification step needed.
    const payload = {
      first_name: values.first_name,
      last_name: values.last_name,
      user_role_id: values.user_role_id,
      password: values.password,
      verified: true,
    };

    try {
      // updateUserInfo returns { verified: true, result: <full_user_data> }
      const updateResult = await (api as any).updateUserInfo(verifyState.userId, payload);

      if (updateResult?.result) {
        // `verified` is a frontend-only flag checked by AppNavigator.
        // The backend doesn't include it in the serialized user — mirror
        // what useLogin does: stamp it on before calling login().
        login({ ...updateResult.result, verified: true });
      } else {
        setSubmitError('Something went wrong, please try again.');
      }
    } catch (e: any) {
      setSubmitError(
        e?.response?.data?.message || e?.message || 'Something went wrong, please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if (verifyState.status === 'loading') {
      return (
        <div className="verification text-center">
          <p>Verifying your invitation…</p>
        </div>
      );
    }

    if (verifyState.status === 'error') {
      return (
        <div className="verification text-center">
          <div className="errorMessage">
            <p>{verifyState.message}</p>
          </div>
          <p>
            <a className="pointer" onClick={() => navigate('/signin')}>Go to Sign In</a>
          </p>
        </div>
      );
    }

    if (verifyState.status === 'existing_user') {
      return (
        <div className="verification text-center">
          <p>
            <strong>{verifyState.email}</strong> already has an account.
            Please sign in to view your invitation.
          </p>
          <button
            type="button"
            className="form-control bgBlue login-btn pointer"
            onClick={() => navigate('/signin')}
          >
            Go to Sign In
          </button>
        </div>
      );
    }

    // new_user — show the registration form
    return (
      <form className="contactForm" onSubmit={handleSubmit(onSubmit)}>
        <h1>Complete your registration</h1>

        <p style={{ color: '#555', marginBottom: 16 }}>
          You were invited as <strong>{verifyState.email}</strong>.
          Fill in your details below to create your account.
        </p>

        {submitError && (
          <div className="errorMessage">
            <p>{submitError}</p>
          </div>
        )}

        {/* Email — read-only */}
        <div className="form-group">
          <label htmlFor="inviteEmail">Email</label>
          <input
            id="inviteEmail"
            type="email"
            className="form-control"
            value={verifyState.email}
            readOnly
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="firstName">First Name <sup>*</sup></label>
          <input
            id="firstName"
            type="text"
            className="form-control"
            {...register('first_name')}
          />
          {errors.first_name && (
            <span className="text-danger">{errors.first_name.message}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name <sup>*</sup></label>
          <input
            id="lastName"
            type="text"
            className="form-control"
            {...register('last_name')}
          />
          {errors.last_name && (
            <span className="text-danger">{errors.last_name.message}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="role">Role <sup>*</sup></label>
          <select
            id="role"
            className="form-control"
            {...register('user_role_id')}
          >
            <option value="">-- Select Role --</option>
            {roles.map((r: any) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          {errors.user_role_id && (
            <span className="text-danger">{errors.user_role_id.message}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="password">Password <sup>*</sup></label>
          <div style={{ position: 'relative' }}>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              className="form-control"
              style={{ paddingRight: 44 }}
              placeholder="8+ chars, uppercase, number, special character"
              {...register('password')}
            />
            <IconButton
              className="showPassword"
              aria-label="toggle password visibility"
              onClick={() => setShowPassword((v) => !v)}
              style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)' }}
              size="small"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </div>
          {errors.password && (
            <span className="text-danger">{errors.password.message}</span>
          )}
          {passwordValue && !errors.password && (
            <div>
              {passwordValue.length < 6 ? (
                <span style={{ color: 'red' }}>Weak password</span>
              ) : passwordRegex.test(passwordValue) ? (
                <span className="strongPassword">Strong password</span>
              ) : (
                <span style={{ color: 'orange' }}>Normal password</span>
              )}
            </div>
          )}
        </div>

        <div className="formSubmission">
          <div className="form-group">
            <button
              type="submit"
              disabled={isSubmitting}
              className="form-control bgBlue login-btn pointer"
            >
              {isSubmitting ? 'Completing registration…' : 'Complete Registration'}
            </button>
          </div>
        </div>

        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <span style={{ color: '#555' }}>Already have an account? </span>
          <a className="pointer" onClick={() => navigate('/signin')}>Sign in</a>
        </div>
      </form>
    );
  };

  return (
    <section className="signUp">
      <div className="container">
        <div className="row">
          <div className="col-md-6">
            <div className="loginImages">
              <div className="loginLogoimg">
                <img src={loginTop} alt="Login top" />
              </div>
              <div className="loginRightimg">
                <img style={{ width: '100%' }} src={loginRight} alt="Login right" />
              </div>
            </div>
          </div>

          <div className="col-md-6">
            {renderContent()}
          </div>
        </div>
      </div>
    </section>
  );
}
